/**
 * Sentry frontend init (PR #83) — env-gated.
 *
 * Em dev (sem VITE_SENTRY_DSN), tudo é no-op silencioso. Em prod com env
 * setado, ativa captura de erros JS + tracing de transações + sessions.
 *
 * Setup:
 *   1. Criar projeto Sentry: https://sentry.io
 *   2. Copiar DSN do projeto JavaScript/React
 *   3. Railway/Vite Variables:
 *        VITE_SENTRY_DSN=https://...@sentry.io/...
 *        VITE_SENTRY_ENV=prod  (ou staging/dev)
 *        VITE_SENTRY_TRACES_RATE=0.1  (10% das transações)
 *
 * Pra import dinâmico, lazy-loaded em main.tsx — Sentry SDK ~50KB, vale só
 * carregar em prod.
 */

/** True se Sentry está configurado em env vars (build-time). */
export function sentryHabilitado(): boolean {
  const dsn = import.meta.env?.VITE_SENTRY_DSN;
  return typeof dsn === "string" && dsn.trim().length > 0;
}

/** Inicializa Sentry SDK frontend. Idempotente. Retorna true se ativou. */
export async function initSentryFrontend(): Promise<boolean> {
  if (!sentryHabilitado()) {
    return false;
  }
  try {
    const Sentry = await import("@sentry/react");
    const dsn = import.meta.env.VITE_SENTRY_DSN as string;
    const env = (import.meta.env.VITE_SENTRY_ENV as string) || "prod";
    const tracesRate = parseFloat(
      (import.meta.env.VITE_SENTRY_TRACES_RATE as string) || "0.1"
    );

    Sentry.init({
      dsn: dsn.trim(),
      environment: env,
      tracesSampleRate: isNaN(tracesRate) ? 0.1 : tracesRate,
      // Replay: capturar sessões de erro pra debugar UX. Custa quota — caro
      // se cliente final usa muito. Default 0 (off) — ativar via env se quiser.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: parseFloat(
        (import.meta.env.VITE_SENTRY_REPLAY_ON_ERROR as string) || "0.0"
      ),
      // Não enviar PII por default.
      sendDefaultPii: false,
      // Filtros: ignorar 4xx esperados, network errors temporários, etc.
      beforeSend(event, hint) {
        // Não enviar 4xx do nosso próprio backend (esperados, não bug)
        const exc = hint.originalException as Error | undefined;
        if (exc?.message?.startsWith("API 4")) {
          return null;
        }
        return event;
      },
    });
    return true;
  } catch (e) {
    console.warn("[sentry] init falhou:", e);
    return false;
  }
}
